import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, FormControlLabel, Switch } from "@material-ui/core";
import { Visibility, VisibilityOff, InfoOutlined } from "@material-ui/icons";
import { InputAdornment, IconButton, Tooltip, Typography, Popover, Box } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    fieldLabelRow: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        marginBottom: 2,
        marginTop: theme.spacing(1),
    },
    fieldLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    helpIcon: {
        fontSize: 15,
        color: theme.palette.text.secondary,
        cursor: "pointer",
        "&:hover": {
            color: theme.palette.primary.main,
        },
    },
    popoverContent: {
        padding: theme.spacing(2),
        maxWidth: 280,
    },
    popoverTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        fontSize: 13,
    },
    popoverText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
}));

const FieldHelp = ({ title, text }) => {
    const classes = useStyles();
    const [anchor, setAnchor] = React.useState(null);
    const toggle = (e) => setAnchor(anchor ? null : e.currentTarget);
    return (
        <>
            <InfoOutlined className={classes.helpIcon} onClick={toggle} />
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <Box className={classes.popoverContent}>
                    <Typography className={classes.popoverTitle}>{title}</Typography>
                    <Typography className={classes.popoverText}>{text}</Typography>
                </Box>
            </Popover>
        </>
    );
};

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    max_tokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number()
        .required("Informe a temperatura")
        .min(0, "Mínimo: 0")
        .max(2, "Máximo: 2"),
    maxTokens: Yup.number()
        .required("Informe o número máximo de tokens")
        .min(50, "Mínimo: 50")
        .max(4000, "Máximo: 4000")
        .integer("Deve ser um número inteiro"),
    apikey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    max_messages: Yup.number()
        .required("Informe o número máximo de mensagens")
        .min(1, "Mínimo: 1")
        .max(100, "Máximo: 100")
        .integer("Deve ser um número inteiro")
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        model: "gpt-4o-mini",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        allowScheduling: false
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values, voice: selectedVoice };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <div className={classes.fieldLabelRow}>
                                    <Typography className={classes.fieldLabel}>API Key</Typography>
                                    <FieldHelp
                                        title="API Key da OpenAI"
                                        text="Chave de autenticação da sua conta OpenAI. Acesse platform.openai.com → API Keys para gerar uma. Cada prompt pode usar uma chave diferente."
                                    />
                                </div>
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.apikey")}
                                        name="apiKey"
                                        type={showApiKey ? 'text' : 'password'}
                                        error={touched.apiKey && Boolean(errors.apiKey)}
                                        helperText={touched.apiKey && errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey}>
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                <div className={classes.fieldLabelRow}>
                                    <Typography className={classes.fieldLabel}>Prompt (instruções da IA)</Typography>
                                    <FieldHelp
                                        title="O que escrever no Prompt"
                                        text="Descreva aqui como a IA deve se comportar: o papel dela, o tom de voz, o que pode ou não responder, e quais situações deve transferir para um humano. Quanto mais detalhado, melhor o atendimento."
                                    />
                                </div>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.prompt")}
                                    name="prompt"
                                    error={touched.prompt && Boolean(errors.prompt)}
                                    helperText={touched.prompt && errors.prompt}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    rows={10}
                                    multiline={true}
                                />
                                <QueueSelectSingle label="Transferir para o departamento (quando a IA não resolver)" />
                                <div className={classes.fieldLabelRow}>
                                    <Typography className={classes.fieldLabel}>Modelo de IA</Typography>
                                    <FieldHelp
                                        title="Qual modelo escolher?"
                                        text={"GPT-4o Mini: mais rápido e econômico, ideal para a maioria dos casos.\nGPT-4o: mais inteligente, recomendado para respostas complexas.\nGPT-4 Turbo: versão anterior do GPT-4, boa capacidade.\nGPT-3.5 Turbo: modelo mais antigo, menor custo."}
                                    />
                                </div>
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <InputLabel>Modelo de IA</InputLabel>
                                    <Field name="model">
                                        {({ field }) => (
                                            <Select {...field} labelWidth={90} multiple={false}>
                                                <MenuItem value="gpt-4o-mini">GPT-4o Mini (Recomendado)</MenuItem>
                                                <MenuItem value="gpt-4o">GPT-4o</MenuItem>
                                                <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
                                                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                                            </Select>
                                        )}
                                    </Field>
                                </FormControl>
                                <div className={classes.fieldLabelRow}>
                                    <Typography className={classes.fieldLabel}>Voz / Text-to-Speech</Typography>
                                    <FieldHelp
                                        title="Resposta por voz (Text-to-Speech)"
                                        text={'Selecione "Texto" para que a IA responda em texto normal.\n\nPara resposta em áudio, escolha uma voz em português e preencha a Chave e Região do Azure Cognitive Services (portal.azure.com → Criar recurso → Speech).\n\nA Chave é a "Subscription Key" e a Região é onde o serviço foi criado (ex: brazilsouth).'}
                                    />
                                </div>
                                <div className={classes.multFieldLine}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                    <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                                        <Select
                                            id="type-select"
                                            labelWidth={60}
                                            name="voice"
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                        >
                                            <MenuItem key={"texto"} value={"texto"}>
                                                Texto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FranciscaNeural"} value={"pt-BR-FranciscaNeural"}>
                                                Francisa
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-AntonioNeural"} value={"pt-BR-AntonioNeural"}>
                                                Antônio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-BrendaNeural"} value={"pt-BR-BrendaNeural"}>
                                                Brenda
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-DonatoNeural"} value={"pt-BR-DonatoNeural"}>
                                                Donato
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ElzaNeural"} value={"pt-BR-ElzaNeural"}>
                                                Elza
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-FabioNeural"} value={"pt-BR-FabioNeural"}>
                                                Fábio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-GiovannaNeural"} value={"pt-BR-GiovannaNeural"}>
                                                Giovanna
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-HumbertoNeural"} value={"pt-BR-HumbertoNeural"}>
                                                Humberto
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-JulioNeural"} value={"pt-BR-JulioNeural"}>
                                                Julio
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeilaNeural"} value={"pt-BR-LeilaNeural"}>
                                                Leila
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-LeticiaNeural"} value={"pt-BR-LeticiaNeural"}>
                                                Letícia
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ManuelaNeural"} value={"pt-BR-ManuelaNeural"}>
                                                Manuela
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-NicolauNeural"} value={"pt-BR-NicolauNeural"}>
                                                Nicolau
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-ValerioNeural"} value={"pt-BR-ValerioNeural"}>
                                                Valério
                                            </MenuItem>
                                            <MenuItem key={"pt-BR-YaraNeural"} value={"pt-BR-YaraNeural"}>
                                                Yara
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.voiceKey")}
                                        name="voiceKey"
                                        error={touched.voiceKey && Boolean(errors.voiceKey)}
                                        helperText={touched.voiceKey && errors.voiceKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.voiceRegion")}
                                        name="voiceRegion"
                                        error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                        helperText={touched.voiceRegion && errors.voiceRegion}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                                
                                <div className={classes.multFieldLine}>
                                    <div style={{ flex: 1 }}>
                                        <div className={classes.fieldLabelRow}>
                                            <Typography className={classes.fieldLabel}>Temperatura</Typography>
                                            <FieldHelp
                                                title="Temperatura"
                                                text="Controla a criatividade das respostas. Valores baixos (0–0.3) deixam as respostas mais diretas e previsíveis. Valores altos (0.8–1) tornam as respostas mais variadas e criativas. Para atendimento, recomendamos entre 0.3 e 0.7."
                                            />
                                        </div>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.temperature")}
                                            name="temperature"
                                            type="number"
                                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            helperText={touched.temperature && errors.temperature || "Entre 0 e 2 (ex: 0.7)"}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div className={classes.fieldLabelRow}>
                                            <Typography className={classes.fieldLabel}>Máximo de Tokens</Typography>
                                            <FieldHelp
                                                title="Máximo de Tokens na resposta"
                                                text="Limita o tamanho da resposta da IA. 1 token ≈ 4 caracteres. Para respostas curtas e objetivas use 100–200. Para respostas mais completas use 300–600. Valores muito altos aumentam o custo por mensagem."
                                            />
                                        </div>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.max_tokens")}
                                            name="maxTokens"
                                            type="number"
                                            inputProps={{ min: 50, max: 4000, step: 50 }}
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            helperText={touched.maxTokens && errors.maxTokens || "50 a 4000"}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                    <div className={classes.fieldLabelRow}>
                                        <Typography className={classes.fieldLabel}>Histórico de Mensagens</Typography>
                                        <FieldHelp
                                            title="Máximo de mensagens no histórico"
                                            text="Quantas mensagens anteriores da conversa a IA considera ao responder. Valores maiores dão mais contexto mas aumentam o custo. Para a maioria dos casos, 10 é suficiente."
                                        />
                                    </div>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_messages")}
                                        name="maxMessages"
                                        type="number"
                                        inputProps={{ min: 1, max: 100, step: 1 }}
                                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                                        helperText={touched.maxMessages && errors.maxMessages || "1 a 100"}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    </div>
                                </div>
                                <div className={classes.fieldLabelRow} style={{ marginTop: 8 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!values.allowScheduling}
                                                onChange={e =>
                                                    setFieldValue("allowScheduling", e.target.checked)
                                                }
                                                color="primary"
                                                name="allowScheduling"
                                            />
                                        }
                                        label="Permitir agendamento pela IA"
                                    />
                                    <FieldHelp
                                        title="Agendamento automático"
                                        text="Quando ativado, a IA pode consultar os serviços e horários livres do módulo de Agenda e criar agendamentos direto na conversa, sempre confirmando com o cliente antes. Requer serviços e profissionais cadastrados na Agenda."
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;